import { lazy, Suspense, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import useSWR from "swr";
import { RouteFallback } from "@/components/route-fallback";
import {
  loadProposal,
  proposalSwrKey,
} from "../proposal-standard/loadProposal";

const standardPageModule = async () =>
  import("../proposal-standard/ProposalPage");
const StandardProposalPage = lazy(standardPageModule);
const LegacyProposalPage = lazy(
  async () => import("../proposal-legacy/ProposalPage"),
);

const SMB_ALIAS_PREFIX = "/vision/proposta-smb";

type ProposalTemplate = "standard" | "legacy";

/**
 * The single client-facing viewer for /vision/proposta/:uuid (and its
 * /vision/proposta-smb alias, kept so already-sent SMB links never break):
 * fetches the proposal first and picks the template by its own
 * `proposal_type` — the type is data, not something a URL can be trusted to
 * carry (PED-12653).
 *
 * Typed proposals (SMB or ENT) render the standard template — ENT gets its
 * extra pricing rows via ProposalVariantProvider inside the page. Legacy/
 * untyped proposals keep the old template until their links expire; it is
 * the only remaining consumer of `features/vision-360/proposal-legacy`. The
 * /proposta-smb alias NEVER falls back to the legacy template — every link
 * ever sent on that path was the standard page, so an untyped or oddly-typed
 * payload behind it must not switch templates on the client. Same reasoning
 * for `preview`: each path keeps rendering the mock it always rendered
 * (alias → standard, /proposta → legacy), so both templates stay reviewable.
 *
 * The SWR key is shared with the standard page's own live flow, so this
 * fetch IS the page's fetch (deduped) — picking a template costs no second
 * request; the legacy branch receives the already-fetched payload as a prop
 * for the same reason. A load error also goes to the standard page: its
 * cover screen owns the retry UX, off the same shared key.
 */
export default function ProposalRoutePage() {
  const { uuid } = useParams<{ uuid: string }>();
  const { pathname } = useLocation();
  const isSmbAlias = pathname.startsWith(SMB_ALIAS_PREFIX);
  // The internal design-review render — no network, each path's own mock.
  const isPreview = uuid === "preview";

  // Download the common-case (standard) chunk while the proposal fetch is in
  // flight instead of serializing chunk behind fetch — both are lazy, so
  // without this every cold open pays fetch + chunk back to back.
  useEffect(() => {
    void standardPageModule();
  }, []);

  const { data: proposal, error } = useSWR(
    !isPreview && uuid ? proposalSwrKey(uuid) : null,
    () => loadProposal(uuid!),
  );

  const resolved = isPreview || proposal !== undefined || error != null;
  // The legacy template only on the canonical path, for a payload that
  // positively exists and has no type — everything else (typed, alias,
  // preview, not-found, load error) is the standard page's to handle.
  const isLegacyUntyped =
    !isSmbAlias &&
    proposal != null &&
    proposal.proposalType !== "SMB" &&
    proposal.proposalType !== "ENT";

  // Latched on the first resolution and never re-decided: SWR keeps
  // revalidating this always-mounted hook (focus, error retry), and a late
  // result flipping the template would unmount the page mid-interaction —
  // wiping a half-typed email gate. Whatever the client saw first, they keep.
  const decidedTemplateRef = useRef<ProposalTemplate | null>(null);
  if (resolved && decidedTemplateRef.current == null) {
    decidedTemplateRef.current = isPreview
      ? isSmbAlias
        ? "standard"
        : "legacy"
      : isLegacyUntyped
        ? "legacy"
        : "standard";
  }
  const template = decidedTemplateRef.current;

  return (
    <Suspense fallback={<RouteFallback variant="proposal" />}>
      {template == null ? (
        <RouteFallback variant="proposal" />
      ) : template === "legacy" ? (
        <LegacyProposalPage initialProposal={proposal ?? null} />
      ) : (
        <StandardProposalPage />
      )}
    </Suspense>
  );
}
